import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiClient, authApi, handleApiError } from '../services/api';

// User types
export type UserType = 'voter' | 'administrator';

export interface User {
  id: string;
  email: string;
  type: UserType;
  role?: string; // For administrators
}

// Auth state interface
interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (data: {
    memberId: string;
    nationalId: string;
    email: string;
    password: string;
    confirmPassword: string;
    firstName: string;
    lastName: string;
    phone?: string;
    acceptedTerms: boolean;
  }) => Promise<void>;
  setUser: (user: User | null) => void;
  clearError: () => void;
}

/**
 * Authentication state store using Zustand
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });

        try {
          const response = await authApi.login({ email, password }) as any;

          // Extract tokens from response data
          const { accessToken, refreshToken } = response.data || response;

          // Store tokens
          apiClient.setTokens(accessToken, refreshToken);

          // Decode JWT to get user info (simplified - in production, validate properly)
          const payload = JSON.parse(atob(accessToken.split('.')[1]));

          const user: User = {
            id: payload.sub,
            email: payload.email,
            type: payload.type,
            role: payload.role,
          };

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          const errorMessage = handleApiError(error);
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: errorMessage,
          });
          throw error;
        }
      },

      logout: async () => {
        set({ isLoading: true });

        try {
          await authApi.logout();
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          // Clear tokens and state
          apiClient.clearTokens();
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        }
      },

      register: async (data) => {
        set({ isLoading: true, error: null });

        try {
          await authApi.register(data);

          // After successful registration, auto-login
          await useAuthStore.getState().login(data.email, data.password);

          set({ isLoading: false, error: null });
        } catch (error) {
          const errorMessage = handleApiError(error);
          set({
            isLoading: false,
            error: errorMessage,
          });
          throw error;
        }
      },

      setUser: (user) => {
        set({
          user,
          isAuthenticated: !!user,
        });
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Session timeout management (30 minutes)
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
let sessionTimer: NodeJS.Timeout | null = null;

export function startSessionTimeout() {
  if (sessionTimer) {
    clearTimeout(sessionTimer);
  }

  sessionTimer = setTimeout(() => {
    const { logout } = useAuthStore.getState();
    logout();
    alert('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
  }, SESSION_TIMEOUT);
}

export function resetSessionTimeout() {
  startSessionTimeout();
}

export function clearSessionTimeout() {
  if (sessionTimer) {
    clearTimeout(sessionTimer);
    sessionTimer = null;
  }
}
